import { useMemo, useState } from "react";
import { IconClock, IconStar, IconShoppingBag, IconGift, IconCalendar } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Chip } from "../../../../components/common/chip";
import { Pagination } from "../../../../components/common/pagination";
import { PageContainer } from "../../../../components/common/page-container";
import { AccentStatCard } from "../../../../components/common/accent-stat-card";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { EmptyState } from "../../../../components/common/empty-state";
import { useMe } from "../../../../swr/useMe";
import { useLoyaltyPointsTransactions } from "../../hooks/useLoyaltyPointsTransactions";
import { useMyLoyaltyRedemptions } from "../../hooks/useMyLoyaltyRedemptions";
import type { LoyaltyPointTransactionDto, LoyaltyPointsMeta, LoyaltyRedemptionDto } from "../../../../api";
import { Link } from "react-router-dom";

const PAGE_SIZE = 10;
const EXPIRY_WARNING_DAYS = 30;
const DAY_MS = 86_400_000;

type TxKind = "earned" | "redeemed" | "expired";

function txKind(tx: LoyaltyPointTransactionDto): TxKind {
  if (tx.isExpired) return "expired";
  if (tx.isRedeemed) return "redeemed";
  return "earned";
}

function formatLongDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function formatShortDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB");
}

function PointsValue({ kind, points }: { kind: TxKind; points: number }) {
  if (kind === "earned")
    return <Text variant="bodyBold" tone="greenDark" inline>+{points} pts</Text>;
  if (kind === "redeemed")
    return <Text variant="bodyBold" tone="inherit" inline className="text-accentError">-{points} pts</Text>;
  return <Text variant="bodyBold" tone="muted" inline>{points} pts</Text>;
}

function StatusChipLocal({ kind }: { kind: TxKind }) {
  if (kind === "expired") return <Chip variant="expired">Expired</Chip>;
  return <Chip variant="approved">Active</Chip>;
}

export function LoyaltyPointsPage() {
  const [page, setPage] = useState(1);
  const { user } = useMe();
  const { data, isLoading } = useLoyaltyPointsTransactions({ page, pageSize: PAGE_SIZE });

  const transactions = (data?.data ?? []) as LoyaltyPointTransactionDto[];
  const meta = data?.meta as LoyaltyPointsMeta | undefined;
  const balance = user?.loyaltyPoints ?? 0;

  const expiring = useMemo(() => {
    const now = Date.now();
    const limit = now + EXPIRY_WARNING_DAYS * DAY_MS;
    const soon = transactions.filter(
      (tx) => !tx.isExpired && !tx.isRedeemed && new Date(tx.expiresAt).getTime() <= limit,
    );
    if (soon.length === 0) return null;
    const points = soon.reduce((sum, tx) => sum + tx.points, 0);
    const earliest = Math.min(...soon.map((tx) => new Date(tx.expiresAt).getTime()));
    const days = Math.max(0, Math.ceil((earliest - now) / DAY_MS));
    return { points, days, date: new Date(earliest) };
  }, [transactions]);

  return (
    <PageContainer>
      <div className="flex flex-col gap-6 max-md:gap-4">
        <div className="flex flex-col gap-1">
          <Heading variant="h2" tone="dark">Loyalty Points</Heading>
          <Text variant="bodySm" tone="muted">Earn points on every purchase and redeem for discounts</Text>
        </div>

        {expiring && (
          <div className="flex items-center gap-4 rounded-2xl border border-accentAmber bg-accentYellowTint px-5 py-4">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-accentAmber">
              <IconClock size={20} className="text-white" />
            </div>
            <div className="flex flex-col gap-0.5">
              <Text variant="bodySmBold" tone="amber">
                {expiring.points} points are expiring in {expiring.days} day{expiring.days === 1 ? "" : "s"} (on{" "}
                {expiring.date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}).
              </Text>
              <Text variant="bodySm" tone="amber">
                Use them before they expire to save on your next order
              </Text>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5 max-md:flex-wrap max-md:gap-3 max-md:px-4 max-md:py-4">
          <div className="flex items-center gap-4">
            <div className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-accentGreenTint">
              <IconStar size={24} className="text-accentGreenDark" />
            </div>
            <div className="flex flex-col gap-0.5">
              <Text variant="bodySm" tone="muted">Current Balance</Text>
              <Heading variant="h1" tone="dark">{balance.toLocaleString()}</Heading>
            </div>
          </div>
          <Chip variant="approved" icon={<IconStar size={13} />}>
            1 pt per QAR 100
          </Chip>
        </div>

        <div className="grid grid-cols-3 gap-4 max-md:grid-cols-1 max-md:gap-3">
          <AccentStatCard label="Total Points Earned" value={(meta?.totalPoints ?? 0).toLocaleString()} />
          <AccentStatCard label="Total Points Redeemed" value={(meta?.redeemedPoints ?? 0).toLocaleString()} />
          <AccentStatCard label="Expired Points" value={(meta?.expiredPoints ?? 0).toLocaleString()} />
        </div>

        <div className="flex flex-col gap-3">
          <Heading variant="h3" tone="dark">Points History</Heading>

          <div className="overflow-hidden rounded-2xl border border-borderDefault bg-bgWhite">
            <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr] gap-4 bg-bgLight px-6 py-3 max-md:hidden">
              <Text variant="capsBig" tone="dark">ACTION</Text>
              <Text variant="capsBig" tone="dark">EXPIRATION DATE</Text>
              <Text variant="capsBig" tone="dark">STATUS</Text>
              <Text variant="capsBig" tone="dark" className="text-right">POINTS</Text>
            </div>

            {isLoading ? (
              <div className="px-6 py-6">
                <ShimmerLoader message="Fetching your loyalty history…" />
              </div>
            ) : transactions.length === 0 ? (
              <EmptyState
                icon={IconStar}
                title="No loyalty points yet"
                description="Earn 1 point for every QAR 100 spent. Your points and redemptions will show up here."
              />
            ) : (
              transactions.map((tx) => {
                const kind = txKind(tx);
                const title =
                  kind === "redeemed" ? "Points redeemed" :
                  kind === "expired"  ? "Points expired"  :
                                        "Points earned";
                const subtitle =
                  kind === "redeemed"
                    ? `Applied as ${tx.currency} ${tx.amount.toFixed(2)} discount`
                    : `${tx.currency} ${tx.amount.toFixed(2)} order`;
                return (
                  // Phones: the action spans the row; expiry, status and points sit below it.
                  <div key={tx.id} className="grid grid-cols-[1.5fr_1fr_1fr_1fr] gap-4 items-center border-t border-borderLight px-6 py-4 max-md:grid-cols-[1fr_auto_auto] max-md:gap-3 max-md:px-4">
                    <div className="flex flex-col gap-0.5 max-md:col-span-3">
                      <Text variant="bodyBold" tone="dark">{title}</Text>
                      <Text variant="bodySm" tone="muted">{subtitle}</Text>
                      <Text variant="bodyXs" tone="muted">{formatLongDate(tx.createdAt)}</Text>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <Text variant="capsSm" tone="muted" className="md:hidden">Expires</Text>
                      <Text variant="body" tone="body">{formatShortDate(tx.expiresAt)}</Text>
                    </div>
                    <div className="flex">
                      <StatusChipLocal kind={kind} />
                    </div>
                    <div className="flex justify-end">
                      <PointsValue kind={kind} points={tx.points} />
                    </div>
                  </div>
                );
              })
            )}

            {meta && meta.pageCount > 1 && (
              <div className="flex justify-center border-t border-borderDefault px-6 py-4">
                <Pagination page={meta.currentPage} pageCount={meta.pageCount} onPageChange={setPage} />
              </div>
            )}
          </div>
        </div>

        <RedemptionsList />

        <div className="flex flex-col gap-8 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5 max-md:gap-5 max-md:px-4">
          <div className="flex items-center gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-accentGreenTint">
              <IconStar size={18} className="text-accentGreenDark" />
            </div>
            <div className="flex flex-col gap-0.5">
              <Heading variant="h3" tone="dark">How Loyalty Points Work</Heading>
              <Text variant="bodySm" tone="muted">Maximize your savings with every purchase</Text>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 max-md:grid-cols-1">
            {[
              { num: 1, icon: <IconShoppingBag size={18} />, title: "Earn Points",    body: <>Get 1 point for every QAR 100 spent on<br />orders</> },
              { num: 2, icon: <IconGift        size={18} />, title: "Redeem Anytime", body: <>Use points at checkout to get instant<br />discounts</> },
              { num: 3, icon: <IconCalendar    size={18} />, title: "Save More",      body: <>Points are valid for 365 days from the<br />date earned</> },
            ].map((step) => (
              <div key={step.num} className="flex items-start gap-3">
                <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-accentGreenTint">
                  <Text variant="bodyXsBold" tone="greenDark" inline>{step.num}</Text>
                </div>
                <div className="flex flex-col gap-1">
                  <Text variant="bodyBold" tone="dark">{step.title}</Text>
                  <Text variant="bodySm" tone="muted">{step.body}</Text>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}

function RedemptionsList() {
  const { data, isLoading } = useMyLoyaltyRedemptions({ page: 1, pageSize: 5 });
  // `saleOrderId` and `isReversed` land on `LoyaltyRedemptionDto` after
  // `npm run generate:api`. Cast lets the page render before the next regen.
  const rows = (data?.data ?? []) as Array<
    LoyaltyRedemptionDto & { saleOrderId?: number | null; isReversed?: boolean }
  >;
  if (isLoading) return null;
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      <Heading variant="h3" tone="dark">Recent Redemptions</Heading>
      <div className="overflow-hidden rounded-2xl border border-borderDefault bg-bgWhite">
        {rows.map((r) => (
          <div
            key={r.id}
            className="flex items-center justify-between border-t border-borderLight px-6 py-3 first:border-t-0"
          >
            <div className="flex flex-col gap-0.5">
              <Text variant="bodyBold" tone="dark">
                {r.saleOrderId != null ? (
                  <Link
                    to={`/orders/${r.saleOrderId}`}
                    className="text-accentGreenDark underline-offset-2 hover:underline"
                  >
                    Order #{r.saleOrderId}
                  </Link>
                ) : (
                  r.reason ?? "Redemption"
                )}
              </Text>
              <Text variant="bodyXs" tone="muted">
                {new Date(r.createdAt).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
                {r.isReversed ? " · Reissued (refunded)" : ""}
              </Text>
            </div>
            <Text variant="bodyBold" tone={r.isReversed ? "muted" : "inherit"}>
              -{r.points.toLocaleString()} pts
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}
